import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { InputNumber } from 'antd';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Button } from '@/components/common/button';
import { AVAILABLE_COUNT_MAX } from '@/lib/constants';
import {
  isRefusedNumericKey,
  isRefusedNumericPaste,
  type NumericInputOptions,
  selectionContext,
} from '@/lib/validation/numeric-input';

import { FormField } from '../form-field';

import styles from './available-count-field.module.scss';

/** A count is digits and nothing else (QA 119717), and never more than `AVAILABLE_COUNT_MAX` — see
 *  `numeric-input.ts` for why both are refused at the keystroke rather than by a `parser`. */
const COUNT_INPUT: NumericInputOptions = {
  decimal: false,
  negative: false,
  max: AVAILABLE_COUNT_MAX,
};

export type AvailableCountFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  required?: boolean;
};

/** Integer 0–99,999 with +/- controls; decrement disabled at 0 / empty, increment at the cap. */
export function AvailableCountField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ name, control, label, required }: AvailableCountFieldProps<TFieldValues, TName>) {
  return (
    <FormField name={name} control={control} label={label} required={required}>
      {({ field, fieldState }) => {
        const current = typeof field.value === 'number' ? field.value : null;
        const atZero = current === null || current <= 0;
        const atMax = current !== null && current >= AVAILABLE_COUNT_MAX;
        return (
          <div className={styles.stepper}>
            <InputNumber
              id={name}
              className={styles.input}
              controls={false}
              min={0}
              max={AVAILABLE_COUNT_MAX}
              step={1}
              precision={0}
              placeholder="Enter Count"
              status={fieldState.error ? 'error' : undefined}
              value={current}
              ref={field.ref}
              onBlur={field.onBlur}
              // `onKeyDown`/`onPaste` are not rc-input-number's own props, so they reach the inner
              // `<input>` through its rest-prop spread — which is where the refusal has to happen.
              onKeyDown={(event) => {
                if (
                  isRefusedNumericKey(event, COUNT_INPUT, selectionContext(event.currentTarget))
                ) {
                  event.preventDefault();
                }
              }}
              onPaste={(event) => {
                const text = event.clipboardData.getData('text');
                if (
                  isRefusedNumericPaste(text, COUNT_INPUT, selectionContext(event.currentTarget))
                ) {
                  event.preventDefault();
                }
              }}
              // `null`, never `undefined`: RHF's `get(_formValues, name, defaultValue)` treats an
              // `undefined` field as absent and hands back the form's default, so on an edit form
              // clearing the input resurrected the seeded count (ADO 114738). `<NumberField>` has
              // always stored antd's own `null` for this reason.
              onChange={(v) => field.onChange(v ?? null)}
            />
            <Button
              htmlType="button"
              variant="outline"
              aria-label="Decrease available count"
              disabled={atZero}
              startIcon={<MinusIcon size={16} />}
              className={[styles.stepBtn, fieldState.error ? styles.stepBtnError : undefined]
                .filter(Boolean)
                .join(' ')}
              onClick={() => field.onChange(Math.max(0, (current ?? 0) - 1))}
            />
            <Button
              htmlType="button"
              variant="outline"
              aria-label="Increase available count"
              disabled={atMax}
              startIcon={<PlusIcon size={16} />}
              className={[styles.stepBtn, fieldState.error ? styles.stepBtnError : undefined]
                .filter(Boolean)
                .join(' ')}
              onClick={() => field.onChange((current ?? 0) + 1)}
            />
          </div>
        );
      }}
    </FormField>
  );
}
